import { useEffect, useMemo, useState } from 'react';
import { PhoneCall, ArrowDownLeft, ArrowUpRight, Pause, MicOff } from 'lucide-react';
import clsx from 'clsx';
import { useLiveCalls, useLeaderAgents } from '@/lib/useLeaderAgents';
import { serverNow } from '@/store/connection';
import type { LiveCall, LeaderAgentRow } from '@/lib/api';

/**
 * Takım Lideri → "Şu an konuşanlar" CANLI çağrı board'u (supervisor monitoring,
 * metadata-only — SES YOK). Hangi agent şu an kiminle, ne yönde, ne kadar süredir
 * çağrıda + beklemede/sessiz mi gösterir. Veri agent softphone'larının WS
 * call_event push'ından ('calls:live'); 1sn tick ile süreler canlı sayar.
 */
export function LiveCallsBoard({ compact = false }: { compact?: boolean }) {
  const { data: calls } = useLiveCalls();
  const { data: agents } = useLeaderAgents();

  // 1sn tick — süreler canlı saysın. serverNow() = backend-senkron saat
  // (süreler server-stamped anchor'lardan sayılır, saat kayması bozmaz).
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    const id = setInterval(() => setNow(serverNow()), 1000);
    return () => clearInterval(id);
  }, []);

  const byId = useMemo(
    () => new Map((agents ?? []).map((a) => [a.id, a])),
    [agents],
  );

  // Çalanlar üstte, en uzun süredir konuşanlar önce.
  const list = useMemo(() => {
    const arr = [...(calls ?? [])];
    arr.sort((a, b) => {
      const ar = a.state === 'in_call' ? 1 : 0;
      const br = b.state === 'in_call' ? 1 : 0;
      if (ar !== br) return br - ar; // konuşanlar önce
      const at = a.serverAnsweredAt ?? a.serverStartedAt ?? 0;
      const bt = b.serverAnsweredAt ?? b.serverStartedAt ?? 0;
      if (at !== bt) return at - bt; // en uzun süredir konuşan önce
      return a.userId - b.userId;    // tiebreaker → deterministik sıralama
    });
    return arr;
  }, [calls]);

  return (
    <section className="bg-surface border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
      <header className="flex items-center gap-2 px-3.5 py-2 border-b border-slate-100 text-[12.5px] font-medium text-slate-900">
        <span className="w-[20px] h-[20px] rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <PhoneCall size={13} />
        </span>
        Şu an konuşanlar
        {list.length > 0 && (
          <span className="inline-flex items-center gap-1.5 ml-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10.5px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> {list.length}
          </span>
        )}
        <span className="ml-auto text-[10.5px] text-slate-400">canlı · ses yok</span>
      </header>

      <div className="p-2">
        {list.length === 0 ? (
          <div className="py-6 text-center text-[11.5px] text-slate-400">Şu an aktif çağrı yok.</div>
        ) : (
          <div className={clsx('grid gap-2', compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3')}>
            {list.map((c) => (
              <LiveCallCard key={c.userId} c={c} agent={byId.get(c.userId)} now={now} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function LiveCallCard({ c, agent, now }: { c: LiveCall; agent?: LeaderAgentRow; now: number }) {
  const ringing = c.state !== 'in_call';
  const name = agent?.displayName ?? c.username ?? '—';
  const ext = agent?.extension ?? '';
  const inbound = c.direction === 'inbound';
  const peerLabel = [c.peer, c.peerName].filter(Boolean).join(' · ') || 'bilinmiyor';
  // Süre SUNUCU-damgalı anchor'dan (server-authoritative) → agent/supervisor
  // saat kayması süreyi bozmaz. now da serverNow() tabanlı.
  const durMs = ringing
    ? (c.serverStartedAt ? now - c.serverStartedAt : 0)
    : (c.serverAnsweredAt ? now - c.serverAnsweredAt : 0);

  return (
    <div className={clsx(
      'rounded-xl ring-1 p-2.5 flex flex-col gap-1.5',
      ringing ? 'bg-amber-50/60 ring-amber-200' : 'bg-emerald-50/50 ring-emerald-200',
    )}>
      {/* Agent satırı */}
      <div className="flex items-center gap-2">
        <div className="relative w-7 h-7 rounded-full bg-gradient-to-br from-slate-500 to-slate-600 text-white flex items-center justify-center text-[10px] font-semibold shrink-0">
          {name.slice(0, 2).toUpperCase()}
          <span className={clsx('absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-base',
            ringing ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse')} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold text-slate-900 truncate leading-tight">{name}</div>
          {ext && <div className="text-[10px] text-slate-400 font-mono leading-tight">{ext}</div>}
        </div>
        <span className={clsx('text-[14px] font-bold tabular-nums leading-none',
          ringing ? 'text-amber-700' : 'text-emerald-700')}>
          {ringing ? 'çalıyor' : fmtDur(durMs)}
        </span>
      </div>

      {/* Karşı taraf + yön */}
      <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
        <span className={clsx('inline-flex items-center justify-center w-4 h-4 rounded shrink-0',
          inbound ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600')}>
          {inbound ? <ArrowDownLeft size={11} /> : <ArrowUpRight size={11} />}
        </span>
        <span className="text-slate-400">{inbound ? 'gelen' : 'giden'}</span>
        <span className="font-medium text-slate-700 truncate">{peerLabel}</span>
        <span className="ml-auto flex items-center gap-1 shrink-0">
          {c.onHold && (
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-sky-100 text-sky-700 text-[9.5px] font-medium">
              <Pause size={9} /> beklemede
            </span>
          )}
          {c.muted && (
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-rose-100 text-rose-700 text-[9.5px] font-medium">
              <MicOff size={9} /> sessiz
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

function fmtDur(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
